import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

const F = "'DM Sans', sans-serif";
const MONO = "'Space Mono', monospace";
const BG = '#0d131f';
const TEXT = '#dde2f4';
const DIM = '#8A9BB5';
const CYAN = '#00D4FF';

const NAV_ITEMS = [
  { id: 'overview', label: 'Overview' },
  { id: 'authentication', label: 'Authentication' },
  { id: 'base-url', label: 'Base URL' },
  { id: 'rate-limiting', label: 'Rate Limiting' },
  { id: 'endpoints', label: 'Endpoints' },
  { id: 'census-id-format', label: 'Census ID Format' },
  { id: 'data-model', label: 'Data Model' },
  { id: 'error-handling', label: 'Error Handling' },
  { id: 'versioning', label: 'Versioning' },
  { id: 'getting-access', label: 'Getting Access' },
];

export default function ApiDocs() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [activeSection, setActiveSection] = useState(NAV_ITEMS[0].id);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    document.title = 'API Documentation | Meridian Registry';
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=Space+Mono&display=swap';
    document.head.appendChild(link);
    return () => { document.title = 'Meridian Registry'; };
  }, []);

  const handleScroll = useCallback(() => {
    const offset = 120;
    for (let i = NAV_ITEMS.length - 1; i >= 0; i--) {
      const el = sectionRefs.current[NAV_ITEMS[i].id];
      if (el && el.getBoundingClientRect().top <= offset) {
        setActiveSection(NAV_ITEMS[i].id);
        return;
      }
    }
    setActiveSection(NAV_ITEMS[0].id);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollTo = (id: string) => {
    const el = sectionRefs.current[id];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div style={{ background: BG, color: TEXT, fontFamily: F, minHeight: '100vh' }}>

      {/* NAV */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
        background: 'rgba(13,19,31,0.8)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        boxShadow: '0 8px 32px rgba(0,229,255,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 2rem', maxWidth: 1536, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <img alt="Meridian" src="/Meridian_M_logo-transparent.png" style={{ height: 32, width: 'auto', objectFit: 'contain', cursor: 'pointer' }}
              onClick={() => navigate('/')} />
            <span style={{ fontSize: '0.875rem', color: DIM, fontWeight: 500 }}>API Documentation</span>
          </div>
          <button onClick={() => navigate('/login')} onMouseEnter={e => {
            e.currentTarget.style.background = 'rgba(0,212,255,0.18)';
            e.currentTarget.style.boxShadow = '0 0 16px rgba(0,212,255,0.3)';
          }} onMouseLeave={e => {
            e.currentTarget.style.background = 'rgba(0,212,255,0.08)';
            e.currentTarget.style.boxShadow = '0 0 8px rgba(0,212,255,0.2)';
          }} style={{
            padding: '0.5rem 1.25rem', borderRadius: 12,
            background: 'rgba(0,212,255,0.08)', color: CYAN, border: `1px solid ${CYAN}`,
            boxShadow: '0 0 8px rgba(0,212,255,0.2)', cursor: 'pointer',
            fontFamily: F, fontSize: '0.875rem', fontWeight: 500, transition: 'all 0.3s ease',
          }}>Login</button>
        </div>
      </nav>

      {/* MAIN CONTENT */}
      <div style={{ display: 'flex', maxWidth: 1280, margin: '0 auto', paddingTop: 80, flexWrap: 'wrap' }}>

        {/* STICKY SIDE NAV — hidden on mobile */}
        {!isMobile && (
          <aside style={{
            width: 220, minWidth: 220, position: 'sticky', top: 90, alignSelf: 'flex-start',
            padding: '2rem 0 2rem 2rem', display: 'flex', flexDirection: 'column', gap: '0.25rem',
          }}>
            {NAV_ITEMS.map(s => (
              <button key={s.id} onClick={() => scrollTo(s.id)} style={{
                background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer',
                fontFamily: F, fontSize: '0.75rem', fontWeight: activeSection === s.id ? 600 : 400,
                color: activeSection === s.id ? CYAN : '#64748b',
                padding: '0.4rem 0.75rem', borderRadius: 6,
                borderLeft: activeSection === s.id ? `2px solid ${CYAN}` : '2px solid transparent',
                transition: 'all 0.2s ease', letterSpacing: '0.02em',
              }}
                onMouseEnter={e => { if (activeSection !== s.id) (e.currentTarget as HTMLElement).style.color = '#94a3b8'; }}
                onMouseLeave={e => { if (activeSection !== s.id) (e.currentTarget as HTMLElement).style.color = '#64748b'; }}
              >
                {s.label}
              </button>
            ))}
          </aside>
        )}

        {/* CONTENT */}
        <main style={{ flex: 1, minWidth: 0, padding: isMobile ? '2rem 1rem 4rem' : '3rem 2rem 4rem' }}>
          <h1 style={{ fontSize: '2.25rem', fontWeight: 700, marginBottom: '0.5rem', color: 'white' }}>
            API Documentation
          </h1>
          <div style={{ height: 4, width: 80, background: CYAN, boxShadow: `0 0 10px ${CYAN}`, marginBottom: '0.75rem' }} />
          <P style={{ marginBottom: '3rem' }}>
            Technical reference for the Meridian Registry REST API.
          </P>

          {/* 1. Overview */}
          <div ref={el => { sectionRefs.current['overview'] = el; }} style={{ scrollMarginTop: 100 }}>
            <Section title="Overview">
              <P>
                The Meridian Registry API provides programmatic access to a deduplicated master registry
                of retail outlets. It enables FMCG manufacturers, distributors, and technology partners
                to look up canonical outlet records, search the registry, analyse distribution gaps,
                and integrate Census ID data directly into ERP, BI, or distribution planning systems.
              </P>
            </Section>
          </div>

          {/* 2. Authentication */}
          <div ref={el => { sectionRefs.current['authentication'] = el; }} style={{ scrollMarginTop: 100 }}>
            <Section title="Authentication">
              <P>
                The API supports two authentication methods. External integrations authenticate
                via an API key passed in the <Code>X-API-Key</Code> request header. Dashboard
                users authenticate with email and password credentials, receiving a JWT bearer
                token for subsequent requests.
              </P>
              <P style={{ marginTop: '0.75rem' }}>
                API keys are issued per client and scoped to your organisation's data.
                Keys are shown once at creation and cannot be retrieved afterward.
                To request an API key, contact <A href="mailto:hello@meridianregistry.com">hello@meridianregistry.com</A>.
              </P>
            </Section>
          </div>

          {/* 3. Base URL */}
          <div ref={el => { sectionRefs.current['base-url'] = el; }} style={{ scrollMarginTop: 100 }}>
            <Section title="Base URL">
              <CodeBlock>https://api.meridianregistry.com/api/v1</CodeBlock>
              <P style={{ marginTop: '0.75rem', fontSize: '0.8rem' }}>
                All endpoint paths in this document are relative to the base URL.
              </P>
            </Section>
          </div>

          {/* 4. Rate Limiting */}
          <div ref={el => { sectionRefs.current['rate-limiting'] = el; }} style={{ scrollMarginTop: 100 }}>
            <Section title="Rate Limiting">
              <P>
                All API endpoints are rate limited. If your application exceeds the permitted
                request volume, the API will respond with HTTP 429. Specific rate limit
                thresholds are provided upon account activation.
              </P>
            </Section>
          </div>

          {/* 5. Endpoints */}
          <div ref={el => { sectionRefs.current['endpoints'] = el; }} style={{ scrollMarginTop: 100 }}>
            <Section title="Endpoints">

              {/* Auth Login */}
              <Endpoint method="POST" path="/auth/login" desc="Authenticate a dashboard user and obtain a session token for subsequent API requests.">
                <ParamList params={[
                  { name: 'email', description: 'Account email address' },
                  { name: 'password', description: 'Account password' },
                ]} label="Request body" />
                <P style={{ marginTop: '1rem', marginBottom: '0.5rem', fontSize: '0.8rem', color: DIM }}>Example response:</P>
                <CodeBlock>{`{
  "success": true,
  "token": "eyJhbGciOiJIUzI1NiIs...",
  "role": "client",
  "name": "Marko Petrovic",
  "email": "m.petrovic@example.com",
  "company": "Balkan Distributors"
}`}</CodeBlock>
              </Endpoint>

              {/* Census ID Lookup */}
              <Endpoint method="GET" path="/outlet/{census_id}" desc="Retrieve the canonical record for a specific retail outlet using its permanent Census ID.">
                <ParamList params={[
                  { name: 'census_id', description: 'Permanent outlet identifier (e.g. CENS-0047291)' },
                ]} label="Path parameters" />
                <P style={{ marginTop: '1rem', marginBottom: '0.5rem', fontSize: '0.8rem', color: DIM }}>Example request:</P>
                <pre style={{
                  fontFamily: MONO, fontSize: '0.8rem', background: '#0a0e1a',
                  border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8,
                  padding: '1rem 1.25rem', overflowX: 'auto', whiteSpace: 'pre', maxWidth: '100%',
                  color: '#c4d4e0', lineHeight: 1.6,
                }}><code style={{ fontFamily: MONO }}>{`curl -X GET "https://api.meridianregistry.com/api/v1/outlet/CENS-0047291" \\
  -H "X-API-Key: your_api_key_here"`}</code></pre>
                <P style={{ marginTop: '1rem', marginBottom: '0.5rem', fontSize: '0.8rem', color: DIM }}>Example response:</P>
                <CodeBlock>{`{
  "data": {
    "census_id": "CENS-0047291",
    "meridian_name": "Mini Market Express",
    "meridian_address": "Main Street 74",
    "city": "Central City",
    "municipality": "Central District",
    "region": "",
    "outlet_format": "Convenience",
    "chain_name": "",
    "is_chain": false,
    "latitude": 45.2551,
    "longitude": 19.8425,
    "status": "active",
    "first_seen": "2025-09-14"
  },
  "meta": {
    "census_id": "CENS-0047291",
    "retrieved_at": "2026-03-27T10:42:18.304521+00:00"
  }
}`}</CodeBlock>
              </Endpoint>

              {/* Outlet Search */}
              <Endpoint method="GET" path="/registry/outlets" desc="Search and filter the outlet registry to find retail locations by name, region, format, or chain.">
                <ParamList params={[
                  { name: 'search', description: 'Free-text search across outlet name and address' },
                  { name: 'region', description: 'Filter by country or region' },
                  { name: 'outlet_format', description: 'Filter by outlet type (e.g. Supermarket, Convenience)' },
                  { name: 'chain_name', description: 'Filter by retail chain' },
                  { name: 'page', description: 'Page number (default 1)' },
                  { name: 'limit', description: 'Results per page (default 25, max 100)' },
                ]} label="Query parameters" />
                <P style={{ marginTop: '1rem', marginBottom: '0.5rem', fontSize: '0.8rem', color: DIM }}>Example response:</P>
                <CodeBlock>{`{
  "outlets": [
    {
      "census_id": "CENS-0012084",
      "name": "Market Plus",
      "address": "Commerce Avenue 18",
      "city": "Metro City",
      "region": "",
      "outlet_format": "Grocery",
      "chain_name": "",
      "sources": 3,
      "latitude": 44.0128,
      "longitude": 20.9114
    }
  ],
  "total": 14829,
  "page": 1,
  "limit": 25
}`}</CodeBlock>
              </Endpoint>

              {/* Registry Statistics */}
              <Endpoint method="GET" path="/stats" desc="Retrieve high-level registry statistics to understand the scope and health of the outlet database.">
                <P style={{ marginTop: '1rem', marginBottom: '0.5rem', fontSize: '0.8rem', color: DIM }}>Example response:</P>
                <CodeBlock>{`{
  "client_records": 27164,
  "active_clients": 9,
  "registry_outlets": 129507,
  "matched_outlets": 25718,
  "pending_review": 7298
}`}</CodeBlock>
              </Endpoint>

              {/* Distribution Gaps */}
              <Endpoint method="GET" path="/gaps/outlets" desc="Identify retail outlets that exist in the registry but are absent from your database, representing distribution opportunities.">
                <ParamList params={[
                  { name: 'client', description: 'Your client identifier (required)' },
                  { name: 'region', description: 'Filter gaps by country or region' },
                  { name: 'format', description: 'Filter gaps by outlet type' },
                  { name: 'city', description: 'Filter gaps by city' },
                  { name: 'page', description: 'Page number (default 1)' },
                  { name: 'limit', description: 'Results per page (default 50, max 200)' },
                ]} label="Query parameters" />
                <P style={{ marginTop: '1rem', marginBottom: '0.5rem', fontSize: '0.8rem', color: DIM }}>Example response:</P>
                <CodeBlock>{`{
  "gaps": [
    {
      "census_id": "CENS-0031748",
      "name": "Corner Kiosk",
      "address": "Station Road 112",
      "city": "Capital City",
      "region": "",
      "outlet_format": "Kiosk",
      "sources": 4,
      "latitude": 44.7866,
      "longitude": 20.4489
    }
  ],
  "total": 3217,
  "page": 1,
  "limit": 50
}`}</CodeBlock>
              </Endpoint>

              {/* Gap Export */}
              <Endpoint method="GET" path="/gaps/export" desc="Download your distribution gap analysis as a spreadsheet, ready for field sales route planning.">
                <ParamList params={[
                  { name: 'client', description: 'Your client identifier (required)' },
                  { name: 'country', description: 'Filter by country' },
                  { name: 'outlet_type', description: 'Filter by outlet type' },
                  { name: 'city', description: 'Filter by city' },
                ]} label="Query parameters" />
                <P style={{ marginTop: '0.75rem', fontSize: '0.85rem' }}>
                  Returns an XLSX file containing gap outlet records with name, address, city,
                  coordinates, and source corroboration data.
                </P>
              </Endpoint>

            </Section>
          </div>

          {/* 6. Census ID Format */}
          <div ref={el => { sectionRefs.current['census-id-format'] = el; }} style={{ scrollMarginTop: 100 }}>
            <Section title="Census ID Format">
              <P>
                Every unique physical outlet in the Meridian registry is assigned a permanent
                identifier in the format <Code>CENS-XXXXXXX</Code>, where <Code>X</Code> is
                a zero-padded digit. For example: <Code>CENS-0047291</Code>.
              </P>
              <P style={{ marginTop: '0.75rem' }}>
                A Census ID is assigned once and never changes, even as the outlet's name,
                address, or ownership evolves across data sources over time. Census IDs are
                the stable key for tracking outlets across systems and data cycles.
              </P>
            </Section>
          </div>

          {/* 7. Data Model */}
          <div ref={el => { sectionRefs.current['data-model'] = el; }} style={{ scrollMarginTop: 100 }}>
            <Section title="Data Model">
              <P style={{ marginBottom: '1rem' }}>
                An outlet record in the Meridian registry contains the following fields:
              </P>
              <div style={{
                background: 'rgba(47,53,66,0.4)', border: '1px solid rgba(0,229,255,0.1)',
                borderRadius: 8, padding: '1.25rem 1.5rem',
              }}>
                <FieldList fields={[
                  { name: 'Census ID', desc: 'Permanent unique identifier for the outlet' },
                  { name: 'Name', desc: 'Canonical outlet name resolved from all data sources' },
                  { name: 'Address', desc: 'Standardised street address' },
                  { name: 'City', desc: 'City or settlement' },
                  { name: 'Municipality', desc: 'Municipality or district' },
                  { name: 'Region', desc: 'Country or administrative region' },
                  { name: 'Outlet Format', desc: 'Category of retail location (e.g. Supermarket, Kiosk, Convenience)' },
                  { name: 'Chain Name', desc: 'Retail chain affiliation, if applicable' },
                  { name: 'Latitude / Longitude', desc: 'GPS coordinates of the outlet' },
                  { name: 'Status', desc: 'Whether the outlet is active or flagged as a probable closure' },
                  { name: 'Sources', desc: 'Number of independent data sources that corroborate this outlet' },
                  { name: 'First Seen', desc: 'Date the outlet first appeared in the registry' },
                ]} />
              </div>
            </Section>
          </div>

          {/* 8. Error Handling */}
          <div ref={el => { sectionRefs.current['error-handling'] = el; }} style={{ scrollMarginTop: 100 }}>
            <Section title="Error Handling">
              <P style={{ marginBottom: '1rem' }}>
                The API uses standard HTTP status codes to indicate the outcome of each request.
              </P>
              <div style={{
                background: 'rgba(47,53,66,0.4)', border: '1px solid rgba(0,229,255,0.1)',
                borderRadius: 8, padding: '1.25rem 1.5rem',
              }}>
                <ErrorList errors={[
                  { code: '400', desc: 'The request was malformed or missing required parameters.' },
                  { code: '401', desc: 'Authentication failed. The API key or session token is missing or invalid.' },
                  { code: '403', desc: 'Access denied. Your credentials do not have permission for this resource.' },
                  { code: '404', desc: 'The requested resource was not found.' },
                  { code: '429', desc: 'Rate limit exceeded. Reduce request frequency and retry after a brief interval.' },
                  { code: '500', desc: 'An internal server error occurred. If persistent, contact support.' },
                ]} />
              </div>
            </Section>
          </div>

          {/* 9. Versioning */}
          <div ref={el => { sectionRefs.current['versioning'] = el; }} style={{ scrollMarginTop: 100 }}>
            <Section title="Versioning">
              <P>
                The API is versioned. Breaking changes will always be introduced under a new
                version path, ensuring existing integrations continue to function without modification.
              </P>
            </Section>
          </div>

          {/* 10. Getting Access */}
          <div ref={el => { sectionRefs.current['getting-access'] = el; }} style={{ scrollMarginTop: 100 }}>
            <Section title="Getting Access">
              <P>
                To request API access, contact us at{' '}
                <A href="mailto:hello@meridianregistry.com">hello@meridianregistry.com</A>{' '}
                or{' '}
                <Link to="/request-demo" style={{ color: CYAN, textDecoration: 'none' }}>submit a demo request</Link>.
              </P>
            </Section>
          </div>

        </main>
      </div>

      {/* FOOTER */}
      <footer style={{
        background: 'rgba(13,19,31,0.8)', backdropFilter: 'blur(4px)',
        width: '100%', padding: '3rem 2rem', borderTop: '1px solid rgba(255,255,255,0.05)',
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '3rem', maxWidth: '80rem', margin: '0 auto', alignItems: 'start' }}>
          <div>
            <h4 style={{ fontFamily: F, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, color: 'white' }}>Meridian Registry</h4>
            <p style={{ color: '#94a3b8', fontFamily: F, fontSize: '0.875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: '1rem', lineHeight: 1.625 }}>Where Retail Meets Intelligence</p>
            <p style={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: '1.5rem', lineHeight: 2 }}>&copy; 2026 Meridian Registry. All rights reserved.</p>
          </div>
          <FooterCol title="Platform" links={[
            { label: 'API Documentation', to: '/api-docs' },
            { label: 'Request a Demo', to: '/request-demo' },
            { label: 'FAQ', to: '/faq' },
            { label: 'Contact Us', href: 'mailto:hello@meridianregistry.com' },
          ]} />
          <FooterCol title="Legal" links={[
            { label: 'Privacy Policy', to: '/privacy' },
            { label: 'Terms of Service', to: '/terms' },
            { label: 'Cookie Policy', to: '/cookies' },
          ]} />
        </div>
      </footer>
    </div>
  );
}

/* ── Section ── */

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: '3rem' }}>
      <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: 'white', marginBottom: '0.4rem' }}>{title}</h2>
      <div style={{ height: 3, width: 50, background: CYAN, boxShadow: `0 0 8px ${CYAN}`, marginBottom: '1.25rem' }} />
      {children}
    </section>
  );
}

/* ── Endpoint Card ── */

function Endpoint({ method, path, desc, children }: { method: string; path: string; desc: string; children: React.ReactNode }) {
  return (
    <div
      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,212,255,0.3)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,212,255,0.1)'; }}
      style={{
        background: '#0F1628', border: '1px solid rgba(0,212,255,0.1)',
        borderRadius: 8, padding: '1.5rem', marginBottom: '1.5rem',
        transition: 'border-color 0.2s ease', width: '100%', boxSizing: 'border-box',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <span style={{
          background: method === 'GET' ? 'rgba(0,212,255,0.15)' : 'rgba(139,92,246,0.2)',
          color: method === 'GET' ? CYAN : '#a78bfa',
          fontFamily: MONO, fontSize: '0.7rem', fontWeight: 700,
          padding: '0.2rem 0.5rem', borderRadius: 4, letterSpacing: '0.04em', flexShrink: 0,
        }}>{method}</span>
        <code style={{ fontFamily: MONO, fontSize: '0.9rem', color: TEXT, wordBreak: 'break-all' }}>{path}</code>
      </div>
      <P style={{ marginBottom: '1rem' }}>{desc}</P>
      {children}
    </div>
  );
}

/* ── Parameter List ── */

function ParamList({ params, label }: { params: { name: string; description: string }[]; label: string }) {
  return (
    <div style={{ marginTop: '0.5rem' }}>
      <P style={{ fontSize: '0.8rem', color: DIM, marginBottom: '0.4rem' }}>{label}:</P>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', paddingLeft: '0.5rem' }}>
        {params.map(p => (
          <div key={p.name} style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
            <Code>{p.name}</Code>
            <span style={{ color: DIM, fontSize: '0.8rem' }}>{p.description}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Field List ── */

function FieldList({ fields }: { fields: { name: string; desc: string }[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
      {fields.map(f => (
        <div key={f.name} style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', flexWrap: 'wrap' }}>
          <span style={{ color: TEXT, fontSize: '0.85rem', fontWeight: 600, minWidth: 160, flexShrink: 0 }}>{f.name}</span>
          <span style={{ color: DIM, fontSize: '0.85rem' }}>{f.desc}</span>
        </div>
      ))}
    </div>
  );
}

/* ── Error List ── */

function ErrorList({ errors }: { errors: { code: string; desc: string }[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
      {errors.map(e => (
        <div key={e.code} style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', flexWrap: 'wrap' }}>
          <Code>{e.code}</Code>
          <span style={{ color: DIM, fontSize: '0.85rem' }}>{e.desc}</span>
        </div>
      ))}
    </div>
  );
}

/* ── Typography helpers ── */

function P({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <p style={{ color: DIM, fontSize: '0.9rem', lineHeight: 1.7, margin: 0, ...style }}>{children}</p>;
}

function Code({ children }: { children: React.ReactNode }) {
  return (
    <code style={{
      fontFamily: MONO, fontSize: '0.8rem', background: 'rgba(0,212,255,0.08)',
      color: CYAN, padding: '0.15rem 0.4rem', borderRadius: 4,
    }}>{children}</code>
  );
}

function CodeBlock({ children }: { children: React.ReactNode }) {
  return (
    <pre style={{
      fontFamily: MONO, fontSize: '0.8rem', background: 'rgba(0,0,0,0.4)',
      border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8,
      padding: '1rem 1.25rem', overflowX: 'auto', whiteSpace: 'pre', maxWidth: '100%',
      color: '#c4d4e0', lineHeight: 1.6,
    }}>{children}</pre>
  );
}

function A({ href, children }: { href: string; children: React.ReactNode }) {
  return <a href={href} style={{ color: CYAN, textDecoration: 'none' }}>{children}</a>;
}

/* ── Footer helpers ── */

type FooterLink = { label: string; to?: string; href?: string };

function FooterCol({ title, links }: { title: string; links: FooterLink[] }) {
  const linkStyle: React.CSSProperties = {
    color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase',
    letterSpacing: '0.1em', fontWeight: 500, cursor: 'pointer',
    transition: 'color 0.2s', textDecoration: 'none',
  };
  const onEnter = (e: React.MouseEvent) => ((e.currentTarget as HTMLElement).style.color = '#67e8f9');
  const onLeave = (e: React.MouseEvent) => ((e.currentTarget as HTMLElement).style.color = '#64748b');

  return (
    <div>
      <h4 style={{ color: '#22d3ee', fontFamily: F, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700 }}>{title}</h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1rem' }}>
        {links.map(link => {
          if (link.to) {
            return <Link key={link.label} to={link.to} style={linkStyle} onMouseEnter={onEnter} onMouseLeave={onLeave}>{link.label}</Link>;
          }
          return <a key={link.label} href={link.href} style={linkStyle} onMouseEnter={onEnter} onMouseLeave={onLeave}>{link.label}</a>;
        })}
      </div>
    </div>
  );
}
